import React, { useMemo } from "react";
import { isEmpty } from "lodash";
import MomentTimeZone from "moment-timezone";
import { Col, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { useQuery } from "react-query";

import {
	HookFormInputGroup,
	HookFormRadioButton,
	HookSelectFormGroup,
} from "components";
import FieldWithLoader from "containers/merchantList/merchantInfo/tabsMerchantInfo/general/fieldWithLoader";
import { DebounceTimeout } from "globalConstants";
import { dropdownValuesService } from "services";
import { trimMidSentenceNormalizer, trimNormalizer } from "validators";
import {
	applyValidatorsIf,
	maxLength2,
	maxValueHours,
	maxValueMinutes,
	minValueHours,
	minValueMinutes,
	normalizeAll,
	onlyMaxDigits2,
	required,
} from "validators/validation";

import styles from "../terminalSolution.module.scss";

type AutoCloseSectionProps = {
	isLoading: boolean;
	sectionName: string;
	timeZoneName: string;
	defaultValues: { [key: string]: any };
};

const AutoCloseSection = ({
	isLoading,
	sectionName,
	timeZoneName,
	defaultValues,
}: AutoCloseSectionProps) => {
	const {
		register,
		watch,

		formState: { errors },
	} = useFormContext();

	const isEnabledAutoClose =
		watch(`${sectionName}.isAutoCloseEnabled`) === "true";

	const { data: medianItems } = useQuery(
		"getMedianItems",
		dropdownValuesService.getMedianItems,
	);

	const { data: timeZone, isLoading: isTimeZoneLoading } = useQuery(
		"getTimeZone",
		() => dropdownValuesService.getTimeZone(timeZoneName),
		{ cacheTime: 0, enabled: !!timeZoneName },
	);

	const timeZoneData = useMemo(() => {
		if (!isEmpty(timeZone)) {
			return `UTC ${MomentTimeZone().tz(timeZone?.id)?.format("Z")} ${
				timeZone?.displayName
			}`;
		} else {
			return `UTC ${MomentTimeZone()
				.tz("America/Los_Angeles")
				.format("Z")} Pacific Standard Time (default time zone)`;
		}
	}, [timeZone]);

	return (
		<div data-autotest="autoCloseSection">
			<p>Auto Close</p>
			<Row className="form-group">
				<Col md={2}>
					<HookFormRadioButton
						id={`${sectionName}.isAutoCloseEnabledYes`}
						register={register}
						name={`${sectionName}.isAutoCloseEnabled`}
						value="true"
						label="Yes"
						blurLoading={isLoading}
					/>
				</Col>
				<Col md={2}>
					<HookFormRadioButton
						id={`${sectionName}.isAutoCloseEnabledNo`}
						register={register}
						name={`${sectionName}.isAutoCloseEnabled`}
						value="false"
						label="No"
						blurLoading={isLoading}
					/>
				</Col>
			</Row>
			{isEnabledAutoClose && (
				<div className={styles.autoCloseContainer}>
					<div className={styles.autoCloseHolder}>
						<div className={styles.autoCloseCol}>
							<HookFormInputGroup
								name={`${sectionName}.autoCloseAtHours`}
								register={register}
								colInput={12}
								blurLoading={isLoading}
								autoComplete="off"
								placeholder="hh"
								validator={applyValidatorsIf(
									() => isEnabledAutoClose,
								)([
									required,
									maxLength2,
									minValueHours,
									maxValueHours,
								])}
								normalize={normalizeAll([
									onlyMaxDigits2,
									trimMidSentenceNormalizer,
								])}
								normalizeOnBlur={trimNormalizer}
								errors={errors}
								defaultValue={defaultValues?.autoCloseAtHours}
							/>
							<i className={styles.timeDivider}>:</i>
						</div>
						<div className={styles.autoCloseCol}>
							<HookFormInputGroup
								name={`${sectionName}.autoCloseAtMinutes`}
								register={register}
								colInput={12}
								blurLoading={isLoading}
								autoComplete="off"
								placeholder="mm"
								validator={applyValidatorsIf(
									() => isEnabledAutoClose,
								)([
									required,
									maxLength2,
									minValueMinutes,
									maxValueMinutes,
								])}
								normalize={normalizeAll([
									onlyMaxDigits2,
									trimMidSentenceNormalizer,
								])}
								normalizeOnBlur={trimNormalizer}
								errors={errors}
								defaultValue={defaultValues?.autoCloseAtMinutes}
							/>
						</div>
						<div className={styles.autoCloseCol}>
							<HookSelectFormGroup
								options={medianItems}
								getOptionLabel={option => option.name}
								getOptionValue={option => option.value}
								placeholder="AM/PM"
								name={`${sectionName}.autoCloseAtMedian`}
								isSearchable={false}
								cacheOptions
								defaultOptions
								defaultValue={defaultValues?.autoCloseAtMedian}
								debounceTimeout={DebounceTimeout}
								blurLoading={isLoading}
								colSelect={{ sm: 12 }}
								hideLabel
								rules={{
									validate: isEnabledAutoClose && {
										required,
									},
								}}
							/>
						</div>
					</div>
					<div className={styles.timeZoneHolder}>
						<FieldWithLoader
							label="Time Zone"
							value={timeZoneData}
							isTimeZoneField={true}
							timeZoneRequestLoading={isTimeZoneLoading}
						/>
					</div>
					<div className={styles.warrningHolder}>
						<span className="text-warning small">
							Must match to Merchant`s timezone
						</span>
					</div>
				</div>
			)}
		</div>
	);
};

export default AutoCloseSection;
